import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  useClearQrCode,
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
  useUploadQrCode,
  type PaymentMethod,
  type PaymentMethodInput,
  type PaymentMethodType,
} from "@/lib/payment-methods";
import { useToast } from "./Toast";

/**
 * Add / Edit a payment method. Type-driven fields:
 *
 *   CASH     → display name + instructions only
 *   BANK     → + provider + account name + account number + QR (optional)
 *   EWALLET  → same as BANK (GCash / PayMaya …)
 *   OTHER    → same as BANK (free-text provider for anything else)
 *
 * QR upload happens as a follow-up POST to /payment-methods/{id}/qr-code
 * after the row exists. For "Add" mode, the operator first clicks Save
 * (which creates the row), then the dialog stays open in "Edit" mode and
 * the QR section becomes interactive.
 */

const TYPE_OPTIONS: { value: PaymentMethodType; label: string; hint: string }[] = [
  { value: "CASH",    label: "Cash / Pay in person", hint: "No account info needed." },
  { value: "BANK",    label: "Bank transfer",        hint: "BPI, BDO, UnionBank, etc." },
  { value: "EWALLET", label: "E-wallet",             hint: "GCash, PayMaya, Maya, etc." },
  { value: "OTHER",   label: "Other",                hint: "Anything else — free-text." },
];

export function PaymentMethodDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  /** null = create mode; non-null = edit mode for that method. */
  editing: PaymentMethod | null;
}) {
  const toast = useToast();
  const create = useCreatePaymentMethod();
  const update = useUpdatePaymentMethod();
  const uploadQr = useUploadQrCode();
  const clearQr = useClearQrCode();

  const [methodType, setMethodType] = useState<PaymentMethodType>("CASH");
  const [provider, setProvider] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [instructions, setInstructions] = useState("");
  const [active, setActive] = useState(true);
  // The created/edited row — once we have an id we can attach a QR.
  const [created, setCreated] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setMethodType(editing.methodType);
      setProvider(editing.provider ?? "");
      setDisplayName(editing.displayName ?? "");
      setAccountName(editing.accountName ?? "");
      setAccountNumber(editing.accountNumber ?? "");
      setInstructions(editing.instructions ?? "");
      setActive(editing.active);
      setCreated(editing);
    } else {
      setMethodType("CASH");
      setProvider("");
      setDisplayName("");
      setAccountName("");
      setAccountNumber("");
      setInstructions("");
      setActive(true);
      setCreated(null);
    }
  }, [open, editing]);

  // Auto-suggest a sensible display name when the operator picks a type
  // and hasn't typed anything yet.
  useEffect(() => {
    if (!open || displayName.trim()) return;
    if (methodType === "CASH") setDisplayName("Cash / Pay in person");
  }, [open, methodType, displayName]);

  const submit = useMutation({
    mutationFn: async () => {
      const body: PaymentMethodInput = {
        methodType,
        provider:
          methodType === "CASH"
            ? "Cash"
            : provider.trim() || undefined,
        displayName: displayName.trim(),
        accountName:
          methodType === "CASH" ? undefined : accountName.trim() || undefined,
        accountNumber:
          methodType === "CASH" ? undefined : accountNumber.trim() || undefined,
        instructions: instructions.trim() || undefined,
        active,
      };
      if (created) {
        return await update.mutateAsync({ id: created.id, ...body });
      } else {
        return await create.mutateAsync(body);
      }
    },
    onSuccess: (saved) => {
      setCreated(saved);
      toast.success(editing ? "Method updated" : "Method created");
      // For create mode, keep the dialog open so the operator can attach
      // a QR if they want. For edit mode, close.
      if (editing) onClose();
    },
    onError: (e) => toast.error("Couldn't save method", (e as Error).message),
  });

  const onPickQr = async (file: File | null) => {
    if (!file || !created) return;
    try {
      await uploadQr.mutateAsync({ id: created.id, file });
      // Refresh the local "created" so the preview thumbnail updates.
      // The mutation invalidates the list cache; we read the new row from
      // the response by re-fetching via update. Easiest: optimistic
      // overwrite of the URL field with the file's blob.
      const blob = URL.createObjectURL(file);
      setCreated({ ...created, qrCodeUrl: blob });
      toast.success("QR uploaded");
    } catch (e) {
      toast.error("QR upload failed", (e as Error).message);
    }
  };

  const onClearQr = async () => {
    if (!created) return;
    try {
      await clearQr.mutateAsync(created.id);
      setCreated({ ...created, qrCodeUrl: null });
      toast.success("QR cleared");
    } catch (e) {
      toast.error("Couldn't clear QR", (e as Error).message);
    }
  };

  if (!open) return null;
  const isCash = methodType === "CASH";
  const canAttachQr = !isCash && created != null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/40" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-lg border bg-popover shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold tracking-tight">
            {editing ? "Edit payment method" : "Add payment method"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isCash
              ? "Cash methods need only a display name + instructions."
              : "Bank / e-wallet / other methods can carry account info + a QR."}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid gap-1.5">
            <Label htmlFor="pm-type">Type</Label>
            <RadioGroup
              value={methodType}
              onValueChange={(v) => setMethodType(v as PaymentMethodType)}
              className="grid grid-cols-2 gap-2"
            >
              {TYPE_OPTIONS.map((t) => {
                const inputId = `pm-type-${t.value}`;
                return (
                  <div key={t.value} className="relative">
                    <RadioGroupItem
                      id={inputId}
                      value={t.value}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={inputId}
                      className="block rounded-md border p-3 text-left cursor-pointer transition-colors border-border hover:bg-muted/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary-light peer-focus-visible:ring-2 peer-focus-visible:ring-ring"
                    >
                      <p className="text-sm font-medium">{t.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {t.hint}
                      </p>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pm-display">Display name</Label>
            <Input
              id="pm-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={isCash ? "Cash / Pay in person" : "BPI · Acme Studio"}
            />
            <p className="text-[11px] text-muted-foreground">
              Shown to customers on the public booking page.
            </p>
          </div>

          {!isCash && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="pm-provider">Provider</Label>
                <Input
                  id="pm-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder="BPI / GCash / PayMaya / …"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="pm-account-name">Account name</Label>
                  <Input
                    id="pm-account-name"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="Acme Studio Inc."
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="pm-account-number">Account number</Label>
                  <Input
                    id="pm-account-number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="1234-5678-9012"
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="pm-instructions">Instructions</Label>
            <textarea
              id="pm-instructions"
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={
                isCash
                  ? "Hand the exact amount to the operator on arrival."
                  : "Send to the account above. Include booking ID in the reference."
              }
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          {!isCash && (
            <div className="grid gap-2">
              <Label>QR code</Label>
              {!canAttachQr && (
                <p className="text-[11px] text-muted-foreground italic">
                  Save the method first, then upload a QR image.
                </p>
              )}
              {canAttachQr && (
                <div className="rounded-md border bg-card p-3 flex items-start gap-3">
                  <div className="h-20 w-20 rounded border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
                    {created?.qrCodeUrl ? (
                      <img
                        src={created.qrCodeUrl}
                        alt="QR code"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <label
                      htmlFor="pm-qr"
                      className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {created?.qrCodeUrl ? "Replace QR…" : "Upload QR…"}
                    </label>
                    <input
                      id="pm-qr"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onPickQr(e.target.files?.[0] ?? null)}
                    />
                    {created?.qrCodeUrl && (
                      <button
                        type="button"
                        onClick={onClearQr}
                        className="block text-xs text-destructive hover:underline"
                      >
                        <Trash2 className="inline h-3 w-3 mr-1" />
                        Remove QR
                      </button>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      PNG / JPG. Shown on the public booking page so customers
                      can scan + pay.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <label className="flex items-start gap-3 rounded-md border bg-card px-3 py-2.5 cursor-pointer">
            <Checkbox
              checked={active}
              onCheckedChange={(v) => setActive(v === true)}
              className="mt-0.5"
              aria-label="Active"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium">Active</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Inactive methods are hidden from new bookings + the Record
                Payment dialog. Existing payments keep their reference.
              </p>
            </div>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {editing ? "Cancel" : "Done"}
          </Button>
          <Button
            size="sm"
            onClick={() => submit.mutate()}
            disabled={submit.isPending || !displayName.trim()}
          >
            {submit.isPending
              ? "Saving…"
              : created && !editing
                ? "Save changes"
                : editing
                  ? "Save changes"
                  : "Create method"}
          </Button>
        </div>
      </div>
    </div>
  );
}
